import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 0;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #injectScheduled = false;
  #injectMutationObserver = new MutationObserver(() => {
    if (this.#injectScheduled) return;
    this.#injectScheduled = true;
    requestAnimationFrame(() => {
      this.#injectScheduled = false;
      this.#injectCustomMegaMenuImages();
    });
  });

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.#injectCustomMegaMenuImages();
    onDocumentLoaded(() => {
      this.#injectCustomMegaMenuImages();
    });

    this.#injectMutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['slot'],
    });

    this.#syncGlobalMenuState(this.ariaExpanded === 'true');
    this.#syncTopLevelLinkOpacity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#injectMutationObserver.disconnect();
    this.#syncGlobalMenuState(false);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    visualItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    // Only treat the actual overflow panel as "hovered" so we can still
    // close mega menus when moving between top-level items.
    return this.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;
    if (item !== this.#state.visualItem) {
      this.#setVisualItem(item);
    }
    if (item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    const previouslyActiveItem = this.#state.activeItem;

    let submenu = findSubmenu(item);
    if ((!submenu || hasDisplayNone(submenu)) && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // If the hovered item has no submenu, make sure the background doesn't "flash"
    // and close any previously opened mega menu.
    if (!(submenu instanceof HTMLElement) || hasDisplayNone(submenu)) {
      this.#debouncedDeactivate.cancel();
      if (previouslyActiveItem) this.#closeActiveSubmenu();
      return;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.style.setProperty('--submenu-opacity', '1');
    this.#syncGlobalMenuState(true);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Clear either the open submenu item or a visual-only hovered item.
    if (item === this.#state.activeItem || item === this.#state.visualItem) {
      this.#debouncedDeactivate(item);
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item) return;
    if (item !== this.#state.activeItem && item !== this.#state.visualItem) return;
    if (!options.force && this.overflowHovered) return;

    this.#closeActiveSubmenu();
    this.#clearVisualItem();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Keep one visual active item and dim its siblings, matching the Maha menu treatment.
   * @param {HTMLElement} item
   */
  #setVisualItem = (item) => {
    const currentListItem = item.closest('.menu-list__list-item');
    if (!(currentListItem instanceof HTMLElement)) return;

    this.#state.visualItem = item;

    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      if (listItem.getAttribute('slot')) return;
      listItem.classList.remove('active', 'non-active');
      listItem.classList.add(listItem === currentListItem ? 'active' : 'non-active');
    });

    this.#syncTopLevelLinkOpacity(currentListItem);
  };

  /**
   * Clear visual dimming classes from top-level menu items.
   */
  #clearVisualItem = () => {
    this.#state.visualItem = null;
    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      listItem.classList.remove('active', 'non-active');
    });

    this.#syncTopLevelLinkOpacity();
  };

  /**
   * Apply Maha-style opacity directly on top-level links for deterministic visual states.
   * @param {HTMLElement | null} [activeListItem]
   */
  #syncTopLevelLinkOpacity = (activeListItem = null) => {
    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      if (listItem.getAttribute('slot')) return;

      const link = listItem.querySelector(':scope > .menu-list__link');
      if (!(link instanceof HTMLElement)) return;

      link.style.opacity = activeListItem ? (listItem === activeListItem ? '0.8' : '0.5') : '0.8';
    });
  };

  /**
   * Sync global menu-open state for overlay and header styling without expensive selector chains.
   * @param {boolean} isOpen
   */
  #syncGlobalMenuState = (isOpen) => {
    document.body.classList.toggle('mega-open', isOpen);

    const headerComponent = this.closest('header-component');
    if (headerComponent instanceof HTMLElement) {
      headerComponent.dataset.megaOpen = isOpen ? 'true' : 'false';
    }

    const headerSection = this.closest('.header-section');
    if (headerSection instanceof HTMLElement) {
      headerSection.dataset.megaOpen = isOpen ? 'true' : 'false';
    }
  };

  /**
   * Close the currently open submenu shell state.
   */
  #closeActiveSubmenu = () => {
    const currentItem = this.#state.activeItem;
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    if (currentItem instanceof HTMLElement) {
      currentItem.ariaExpanded = 'false';
    }
    this.#syncGlobalMenuState(false);
  };

  /**
   * Inject custom mega-menu image cards from hidden template blocks.
   * This is a runtime fallback for contexts where block settings are not directly exposed in Liquid.
   */
  #injectCustomMegaMenuImages = () => {
    const templates = Array.from(
      this.querySelectorAll('template[data-mega-menu-custom-image][data-menu-handle]')
    );

    if (!templates.length) return;

    const defaultLayout = normalizeCustomMediaLayout(this.dataset.customMediaLayoutDefault, 'stack');
    const defaultColumnsDesktop = parsePositiveInt(this.dataset.customMediaColumnsDefault, 2);
    const defaultWidth = normalizeCustomMediaWidth(this.dataset.customMediaWidthDefault, 'medium');

    /** @type {Map<string, { layout: string, width: string, columnsDesktop: number }>} */
    const configByHandle = new Map();
    this.querySelectorAll('[data-mega-menu-trigger-config][data-menu-handle]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const menuHandle = normalizeHandle(element.dataset.menuHandle);
      if (!menuHandle) return;

      configByHandle.set(menuHandle, {
        layout: normalizeCustomMediaLayout(element.dataset.mediaLayout, defaultLayout),
        width: normalizeCustomMediaWidth(element.dataset.mediaWidth, defaultWidth),
        columnsDesktop: parsePositiveInt(element.dataset.mediaColumnsDesktop, defaultColumnsDesktop),
      });
    });

    /** @type {Map<string, HTMLTemplateElement[]>} */
    const templatesByHandle = new Map();

    templates.forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;
      const menuHandle = normalizeHandle(template.dataset.menuHandle);
      if (!menuHandle) return;

      const list = templatesByHandle.get(menuHandle) ?? [];
      list.push(template);
      templatesByHandle.set(menuHandle, list);
    });

    const menuLinks = Array.from(this.querySelectorAll('[ref="menuitem"][aria-controls]'));

    menuLinks.forEach((menuLink) => {
      if (!(menuLink instanceof HTMLAnchorElement)) return;

      const submenu = findSubmenu(menuLink);
      if (!(submenu instanceof HTMLElement)) return;

      const grid = submenu.querySelector('.mega-menu__grid');
      if (!(grid instanceof HTMLElement)) return;
      grid.style.paddingInline = '24px';

      const existingImageLists = Array.from(grid.querySelectorAll('.mega-menu__content-list--images'));
      if (existingImageLists.length) {
        grid.dataset.hasCustomMedia = 'true';
        const hasServerRenderedImages = existingImageLists.some(
          (imageList) => imageList.dataset.injectedCustom !== 'true'
        );

        // If section re-rendering added server-rendered media after a runtime injection,
        // remove stale injected lists to avoid duplicate custom cards.
        if (hasServerRenderedImages) {
          existingImageLists
            .filter((imageList) => imageList.dataset.injectedCustom === 'true')
            .forEach((imageList) => imageList.closest('.mega-menu__content')?.remove());
        }

        return;
      }

      // Preserve featured product/collection layouts.
      if (grid.querySelector('.mega-menu__content-list--products, .mega-menu__content-list--collections')) return;

      const titleHandle = normalizeHandle(menuLink.textContent);
      const hrefHandle = normalizeHandle(extractHandleFromHref(menuLink.getAttribute('href')));
      const matchedTemplates = templatesByHandle.get(titleHandle) ?? templatesByHandle.get(hrefHandle);
      if (!matchedTemplates?.length) return;
      const matchedConfig = configByHandle.get(titleHandle) ?? configByHandle.get(hrefHandle);
      const layout = matchedConfig?.layout ?? defaultLayout;
      const width = matchedConfig?.width ?? defaultWidth;
      const columnsDesktop = matchedConfig?.columnsDesktop ?? defaultColumnsDesktop;

      const content = document.createElement('span');
      content.className = 'mega-menu__content';
      content.dataset.customMediaLayout = layout;
      content.dataset.customMediaWidth = width;
      content.style.setProperty('--menu-content-columns-desktop', String(columnsDesktop));
      content.style.setProperty('--menu-content-columns-tablet', layout === 'grid' ? '2' : '1');
      content.style.gridRow = '1';

      const list = document.createElement('ul');
      list.className = 'mega-menu__content-list mega-menu__content-list--images list-unstyled';
      list.dataset.injectedCustom = 'true';
      list.dataset.customMedia = 'true';
      list.dataset.mediaLayout = layout;

      const textList = grid.querySelector('.mega-menu__list');
      const textColumnsDesktop = Math.max(1, 6 - columnsDesktop);
      const textColumnsTablet = Math.max(1, 4 - (layout === 'grid' ? 2 : 1));
      if (textList instanceof HTMLElement) {
        textList.style.gridRow = '1';
        textList.style.gridColumn = `1 / span ${window.innerWidth >= 990 ? textColumnsDesktop : textColumnsTablet}`;
      }

      matchedTemplates.forEach((template) => {
        const firstElement = template.content.firstElementChild;
        if (!(firstElement instanceof HTMLElement)) return;

        const clonedItem = /** @type {HTMLElement} */ (firstElement.cloneNode(true));
        if (template.dataset.linkEmpty === 'true') {
          const link = clonedItem.querySelector('.resource-card__link');
          if (link instanceof HTMLAnchorElement) {
            link.href = menuLink.href;
          }
        }

        list.append(clonedItem);
      });

      if (!list.childElementCount) return;
      if (layout === 'hero' && list.childElementCount > 1) {
        content.style.setProperty('--menu-content-columns-desktop', '2');
      }
      const imageSizes = getCustomMediaSizes(list.childElementCount);
      list.querySelectorAll('img').forEach((image) => {
        if (!(image instanceof HTMLImageElement)) return;
        image.sizes = imageSizes;
      });
      list.dataset.mediaCount = String(list.childElementCount);
      grid.dataset.hasCustomMedia = 'true';
      content.append(list);
      grid.append(content);
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Prefer the first overflowing item that actually has a submenu.
    // Falling back to the first overflow item can open an empty panel when the
    // first item is a simple link without children.
    const overflowItems = Array.from(element.parentElement?.querySelectorAll('[slot="overflow"]') ?? []);
    const firstOverflowWithSubmenu = overflowItems.find((item) => item.querySelector('[ref="submenu[]"]'));

    return findMenuItem(firstOverflowWithSubmenu ?? overflowItems[0] ?? null);
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Checks if the element is explicitly hidden by display:none.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function hasDisplayNone(element) {
  if (!(element instanceof Element)) return true;
  return window.getComputedStyle(element).display === 'none';
}

/**
 * Convert a menu label/handle into a normalized key for matching.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeHandle(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .trim()
    .replace(/-mega-menu/g, '')
    .replace(/-mega menu/g, '')
    .replace(/ mega-menu/g, '')
    .replace(/ mega menu/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Normalize a custom media layout value to a supported option.
 * @param {string | null | undefined} value
 * @param {string} [fallback]
 * @returns {'single' | 'stack' | 'grid' | 'hero'}
 */
function normalizeCustomMediaLayout(value, fallback = 'stack') {
  if (value === 'single' || value === 'stack' || value === 'grid' || value === 'hero') return value;
  if (fallback === 'single' || fallback === 'stack' || fallback === 'grid' || fallback === 'hero') return fallback;
  return 'stack';
}

/**
 * Normalize a custom media width value to a supported option.
 * @param {string | null | undefined} value
 * @param {string} [fallback]
 * @returns {'small' | 'medium' | 'large'}
 */
function normalizeCustomMediaWidth(value, fallback = 'medium') {
  if (value === 'small' || value === 'medium' || value === 'large') return value;
  if (fallback === 'small' || fallback === 'medium' || fallback === 'large') return fallback;
  return 'medium';
}

/**
 * Parse a positive integer from a string-like value with a fallback.
 * @param {string | null | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function parsePositiveInt(value, fallback) {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Mirror Extra Butter's custom media image request sizing.
 * Menus with two cards use a 5-column desktop rhythm, three cards use 6.
 * @param {number} mediaCount
 * @returns {string}
 */
function getCustomMediaSizes(mediaCount) {
  const desktopDivisor = mediaCount > 0 && mediaCount < 3 ? 5 : 6;
  return `(min-width: 1024px) calc(100vw / ${desktopDivisor}), calc(100vw / 2)`;
}

/**
 * Extract last path segment as a handle candidate.
 * @param {string | null | undefined} href
 * @returns {string}
 */
function extractHandleFromHref(href) {
  if (!href) return '';
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);
    return segments.at(-1) ?? '';
  } catch {
    return '';
  }
}
